import React, { useState, useEffect, useRef } from 'react';
import ReactDOM from 'react-dom';
import { useMap } from '../../context/MapContext';
import { MUNICIPALITIES } from '../MunicipalitySelector/MunicipalitySelector';
import './WelcomePopup.css';

/* ── Passos do wizard ────────────────────────────────────── */
const STEP = { GREETING: 0, ROLE: 1, PERIOD: 2, CITY: 3 };

/* ── Perfis de acesso ────────────────────────────────────── */
const ROLES = [
  {
    id: 'moderador',
    label: 'Moderador',
    desc: 'Servidor de ente regulador',
    color: '#c0392b',
    requiresLogin: true,
  },
  {
    id: 'gestor_municipal',
    label: 'Gestor Municipal',
    desc: 'Servidor de prefeitura ou câmara',
    color: '#2980b9',
    requiresLogin: true,
  },
  {
    id: 'prestadora',
    label: 'Prestadora',
    desc: 'Empresa ou autarquia de saneamento',
    color: '#27ae60',
    requiresLogin: true,
  },
  {
    id: 'sociedade',
    label: 'Sociedade',
    desc: 'Cidadão, pesquisador ou estudante',
    color: '#0d7c8a',
    requiresLogin: false,
  },
];

/* ── Horizontes do PMSB ─────────────────────────────────── */
const PERIODS = [
  { id: 'situacional', years: '2024–2026', phase: 'Elaboração do PMSB', label: 'SITUACIONAL', color: '#0d7c8a', colorDark: '#0a6370', colorBg: '#e0f5f7' },
  { id: 'imediata',    years: '2027–2029', phase: 'Implementação',       label: 'IMEDIATA',    color: '#c8960a', colorDark: '#a87c08', colorBg: '#fef9e0' },
  { id: 'curto',       years: '2030–2034', phase: 'Implementação',       label: 'CURTO PRAZO', color: '#c86020', colorDark: '#a84d18', colorBg: '#fef0e0' },
  { id: 'medio',       years: '2035–2038', phase: 'Implementação',       label: 'MÉDIO PRAZO', color: '#8b3a30', colorDark: '#722f26', colorBg: '#fde8e6' },
  { id: 'longo',       years: '2039–2046', phase: 'Implementação',       label: 'LONGO PRAZO', color: '#5c6e25', colorDark: '#4a591e', colorBg: '#ecf1e0' },
];

/* ── Bandeiras dos estados ──────────────────────────────── */
const STATE_FLAGS = {
  BA: 'https://upload.wikimedia.org/wikipedia/commons/thumb/2/28/Bandeira_da_Bahia.svg/40px-Bandeira_da_Bahia.svg.png',
  PE: 'https://upload.wikimedia.org/wikipedia/commons/thumb/5/59/Bandeira_de_Pernambuco.svg/40px-Bandeira_de_Pernambuco.svg.png',
  MG: 'https://upload.wikimedia.org/wikipedia/commons/thumb/f/f4/Bandeira_de_Minas_Gerais.svg/40px-Bandeira_de_Minas_Gerais.svg.png',
  SP: 'https://upload.wikimedia.org/wikipedia/commons/thumb/2/2b/Bandeira_do_estado_de_S%C3%A3o_Paulo.svg/40px-Bandeira_do_estado_de_S%C3%A3o_Paulo.svg.png',
  CE: 'https://upload.wikimedia.org/wikipedia/commons/thumb/2/2e/Bandeira_do_Cear%C3%A1.svg/40px-Bandeira_do_Cear%C3%A1.svg.png',
};

function StateFlag({ state, size = 32 }) {
  const url = STATE_FLAGS[state];
  if (!url) return <span className="wp-mun-card-state">{state}</span>;
  return (
    <img
      src={url}
      alt={`Bandeira ${state}`}
      className="wp-mun-flag"
      style={{ height: size }}
      loading="lazy"
    />
  );
}

function normalize(s) {
  return s.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase();
}

/* ── Ícones SVG dos perfis ──────────────────────────────── */
function IconShield() {
  return (
    <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/>
    </svg>
  );
}
function IconBuilding() {
  return (
    <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <rect x="3" y="9" width="18" height="13"/><path d="M21 9l-9-7-9 7"/>
      <polyline points="9 22 9 12 15 12 15 22"/>
    </svg>
  );
}
function IconWrench() {
  return (
    <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M14.7 6.3a1 1 0 0 0 0 1.4l1.6 1.6a1 1 0 0 0 1.4 0l3.77-3.77a6 6 0 0 1-7.94 7.94l-6.91 6.91a2.12 2.12 0 0 1-3-3l6.91-6.91a6 6 0 0 1 7.94-7.94l-3.76 3.76z"/>
    </svg>
  );
}
function IconUsers() {
  return (
    <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/>
      <circle cx="9" cy="7" r="4"/>
      <path d="M23 21v-2a4 4 0 0 0-3-3.87"/>
      <path d="M16 3.13a4 4 0 0 1 0 7.75"/>
    </svg>
  );
}

const ROLE_ICONS = {
  moderador:        <IconShield />,
  gestor_municipal: <IconBuilding />,
  prestadora:       <IconWrench />,
  sociedade:        <IconUsers />,
};

/* ── Componente principal ───────────────────────────────── */
export default function WelcomePopup({ onDismiss, onOpenLogin }) {
  const { setSelectedMunicipality, setSelectedPeriod } = useMap();

  const [step,         setStep]         = useState(STEP.GREETING);
  const [period,       setPeriod]       = useState(null);
  const [municipality, setMunicipality] = useState(null);
  const [search,       setSearch]       = useState('');
  const [closing,      setClosing]      = useState(false);

  const mainBtnRef = useRef(null);

  /* Foca o botão principal ao mudar de passo */
  useEffect(() => {
    const t = setTimeout(() => mainBtnRef.current?.focus(), 80);
    return () => clearTimeout(t);
  }, [step]);

  /* ESC → dismiss sem seleção */
  useEffect(() => {
    function onKey(e) {
      if (e.key === 'Escape') handleDismiss();
    }
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  /* Ao entrar no passo de período, pré-seleciona o situacional */
  useEffect(() => {
    if (step === STEP.PERIOD && !period) {
      const s = PERIODS.find((p) => p.id === 'situacional');
      if (s) setPeriod(s);
    }
  }, [step]); // eslint-disable-line react-hooks/exhaustive-deps

  const filteredMuns = MUNICIPALITIES.filter((m) => {
    const q = normalize(search);
    return !q || normalize(m.name).includes(q) || normalize(m.state).includes(q);
  });

  function handleDismiss() {
    setClosing(true);
    setTimeout(() => onDismiss(), 300);
  }

  function handleAccess() {
    if (!period || !municipality) return;
    /* Marca o MascoteWelcome como já exibido para não aparecer logo depois */
    localStorage.setItem('infovivo_welcome_shown', '1');
    setClosing(true);
    setTimeout(() => {
      setSelectedPeriod(period);
      setSelectedMunicipality(municipality);
    }, 300);
  }

  function handleSelectPeriod(p) {
    setPeriod(p);
    setMunicipality(null);
  }

  function handleRoleSelect(role) {
    if (role.requiresLogin) {
      setClosing(true);
      setTimeout(() => {
        onDismiss();
        onOpenLogin();
      }, 300);
    } else {
      setStep(STEP.PERIOD);
    }
  }

  return ReactDOM.createPortal(
    <div
      className={`wp-backdrop${closing ? ' wp-closing' : ''}`}
      role="dialog"
      aria-modal="true"
      aria-labelledby="wp-dialog-title"
    >
      <div className="wp-card">

        {/* ════════════════════════════════════════════════
            PASSO 0 — SAUDAÇÃO DO GUTO
            ════════════════════════════════════════════════ */}
        {step === STEP.GREETING && (
          <>
            {/* Cabeçalho simples */}
            <div className="wp-header wp-header--plain">
              <span className="wp-header-brand" id="wp-dialog-title">
                Painel InfoVivo
              </span>
              <button
                className="wp-close-btn"
                onClick={handleDismiss}
                aria-label="Fechar e configurar depois"
              >
                ✕
              </button>
            </div>

            {/* Corpo da saudação */}
            <div className="wp-greeting">
              <div className="wp-greeting-content">
                <div className="wp-bubble wp-bubble--up">
                  <p>
                    Olá! 👋 Sou o <strong>Guto</strong>, seu guia no{' '}
                    <strong>Painel Interativo InfoVivo</strong>.
                  </p>
                  <p>
                    Aqui você pode explorar dados de saneamento básico,
                    visualizar mapas interativos e acompanhar a implementação
                    dos Planos Municipais de Saneamento Básico (PMSB) em 100
                    municípios brasileiros.
                  </p>
                  <p>Vamos começar?</p>
                </div>

                <button
                  ref={mainBtnRef}
                  className="wp-btn wp-btn--cta"
                  onClick={() => setStep(STEP.ROLE)}
                >
                  Entendido! &rarr;
                </button>
              </div>

              <img
                src="/mascote.png"
                alt="Guto — mascote do Painel InfoVivo"
                className="wp-mascote-lg"
              />
            </div>
          </>
        )}

        {/* ════════════════════════════════════════════════
            PASSOS 1 e 2 — cabeçalho com Guto mini + balão
            ════════════════════════════════════════════════ */}
        {step !== STEP.GREETING && (
          <>
            <div className="wp-header wp-header--guide">
              <div className="wp-header-guide-inner">
                <img
                  src="/mascote.png"
                  alt="Guto"
                  className="wp-mascote-sm"
                  aria-hidden="true"
                />
                <div className="wp-bubble wp-bubble--left" id="wp-dialog-title">
                  {step === STEP.ROLE && (
                    <p>
                      Qual é o seu <strong>perfil de acesso</strong>? Isso nos
                      ajuda a personalizar sua experiência.
                    </p>
                  )}
                  {step === STEP.PERIOD && (
                    <p>
                      Selecione o <strong>horizonte de planejamento</strong> do
                      PMSB que deseja explorar:
                    </p>
                  )}
                  {step === STEP.CITY && (
                    <p>
                      Ótimo! Agora escolha o <strong>município</strong> que
                      deseja analisar:
                    </p>
                  )}
                </div>
              </div>
              <button
                className="wp-close-btn"
                onClick={handleDismiss}
                aria-label="Fechar e configurar depois"
              >
                ✕
              </button>
            </div>

            <div className="wp-body">

              {/* ──────────────────────────────────────────
                  PASSO 1 — Seleção de perfil
                  ────────────────────────────────────────── */}
              {step === STEP.ROLE && (
                <>
                  <div className="wp-role-grid" role="group" aria-label="Selecione seu perfil de acesso">
                    {ROLES.map((r) => (
                      <button
                        key={r.id}
                        className="wp-role-card"
                        style={{ '--role-color': r.color }}
                        onClick={() => handleRoleSelect(r)}
                        aria-label={r.label}
                      >
                        <span className="wp-role-icon" style={{ color: r.color }}>
                          {ROLE_ICONS[r.id]}
                        </span>
                        <span className="wp-role-label">{r.label}</span>
                        <span className="wp-role-desc">{r.desc}</span>
                        {r.requiresLogin && (
                          <span className="wp-role-login-badge" aria-label="Requer login">
                            Requer login
                          </span>
                        )}
                      </button>
                    ))}
                  </div>

                  <div className="wp-nav">
                    <button className="wp-btn wp-btn--ghost" onClick={() => setStep(STEP.GREETING)}>
                      &larr; Voltar
                    </button>
                  </div>
                </>
              )}

              {/* ──────────────────────────────────────────
                  PASSO 2 — Horizonte de planejamento
                  ────────────────────────────────────────── */}
              {step === STEP.PERIOD && (
                <>
                  <div className="wp-timeline" role="group" aria-label="Horizontes do PMSB">
                    {PERIODS.map((p, i) => {
                      const isLocked = p.id !== 'situacional';
                      return (
                      <button
                        key={p.id}
                        className={`wp-chevron${period?.id === p.id ? ' wp-chevron--selected' : ''}${isLocked ? ' wp-chevron--locked' : ''}`}
                        style={{
                          '--clr':      p.color,
                          '--clr-dark': p.colorDark,
                          '--idx':      i,
                        }}
                        onClick={() => !isLocked && handleSelectPeriod(p)}
                        aria-pressed={period?.id === p.id}
                        disabled={isLocked}
                        title={isLocked ? 'Horizonte não disponível nesta versão' : undefined}
                      >
                        <span className="wp-chevron-years">{p.years}</span>
                        <span className="wp-chevron-label">{p.label}</span>
                        <span className="wp-chevron-sub">{p.phase}</span>
                      </button>
                      );
                    })}
                  </div>

                  {period && (
                    <div
                      className="wp-period-info"
                      style={{ '--clr': period.color, '--clr-bg': period.colorBg }}
                      aria-live="polite"
                      aria-atomic="true"
                    >
                      <span className="wp-period-badge" style={{ background: period.color }}>
                        {period.label}
                      </span>
                      <span className="wp-period-range">{period.years}</span>
                      <span className="wp-period-phase">— {period.phase}</span>
                    </div>
                  )}

                  <div className="wp-nav">
                    <button className="wp-btn wp-btn--ghost" onClick={() => setStep(STEP.ROLE)}>
                      &larr; Voltar
                    </button>
                    <button
                      ref={mainBtnRef}
                      className="wp-btn wp-btn--cta"
                      disabled={!period}
                      onClick={() => setStep(STEP.CITY)}
                    >
                      Próximo &rarr;
                    </button>
                  </div>
                </>
              )}

              {/* ──────────────────────────────────────────
                  PASSO 3 — Município
                  ────────────────────────────────────────── */}
              {step === STEP.CITY && (
                <>
                  <div className="wp-mun-search-row">
                    <svg
                      className="wp-mun-search-icon"
                      width="14" height="14" viewBox="0 0 24 24"
                      fill="none" stroke="currentColor"
                      strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"
                      aria-hidden="true"
                    >
                      <circle cx="11" cy="11" r="8" />
                      <line x1="21" y1="21" x2="16.65" y2="16.65" />
                    </svg>
                    <input
                      className="wp-mun-search"
                      type="search"
                      placeholder="Buscar município..."
                      value={search}
                      onChange={(e) => setSearch(e.target.value)}
                      aria-label="Buscar município"
                    />
                  </div>

                  <div className="wp-mun-grid" role="listbox" aria-label="Municípios disponíveis">
                    {filteredMuns.length === 0 && (
                      <p className="wp-mun-empty">Nenhum município encontrado.</p>
                    )}
                    {filteredMuns.map((m) => (
                      <button
                        key={m.code}
                        className={`wp-mun-card${municipality?.code === m.code ? ' wp-mun-card--selected' : ''}`}
                        onClick={() => setMunicipality(m)}
                        role="option"
                        aria-selected={municipality?.code === m.code}
                      >
                        <StateFlag state={m.state} size={32} />
                        <span className="wp-mun-card-name">{m.name}</span>
                        <span className="wp-mun-card-state">{m.state}</span>
                        {municipality?.code === m.code && (
                          <span className="wp-mun-card-check" aria-hidden="true">✓</span>
                        )}
                      </button>
                    ))}
                  </div>

                  <div className="wp-nav">
                    <button className="wp-btn wp-btn--ghost" onClick={() => setStep(STEP.PERIOD)}>
                      ← Voltar
                    </button>
                    <button
                      ref={mainBtnRef}
                      className={`wp-btn wp-btn--cta${!municipality ? ' wp-btn--cta-disabled' : ''}`}
                      disabled={!municipality}
                      onClick={handleAccess}
                    >
                      Acessar o Painel &rarr;
                    </button>
                  </div>
                </>
              )}

            </div>
          </>
        )}
      </div>
    </div>,
    document.body
  );
}
